/**
 * 采集批次相关类型定义
 * 用于素材包（采集批次）的展示和管理
 */

// ==================== 批次状态枚举 ====================

/**
 * 采集批次状态
 */
export const CRAWL_BATCH_STATUS = {
  UPLOADING: 'uploading',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const

export type CrawlBatchStatus =
  (typeof CRAWL_BATCH_STATUS)[keyof typeof CRAWL_BATCH_STATUS]

/**
 * 批次状态中文映射
 */
export const CRAWL_BATCH_STATUS_MAP: Record<
  CrawlBatchStatus,
  { text: string; color: string; bgColor: string }
> = {
  [CRAWL_BATCH_STATUS.UPLOADING]: {
    text: '上传中',
    color: 'text-blue-700 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-500/20',
  },
  [CRAWL_BATCH_STATUS.COMPLETED]: {
    text: '已完成',
    color: 'text-emerald-700 dark:text-emerald-400',
    bgColor: 'bg-emerald-100 dark:bg-emerald-500/20',
  },
  [CRAWL_BATCH_STATUS.FAILED]: {
    text: '失败',
    color: 'text-red-700 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-500/20',
  },
}

// ==================== 批次平台枚举 ====================

/**
 * 采集批次平台
 */
export const CRAWL_BATCH_PLATFORM = {
  TEMU: 'temu',
  AMAZON: 'amazon',
} as const

export type CrawlBatchPlatform =
  (typeof CRAWL_BATCH_PLATFORM)[keyof typeof CRAWL_BATCH_PLATFORM]

/**
 * 批次平台中文映射
 */
export const CRAWL_BATCH_PLATFORM_MAP: Record<
  CrawlBatchPlatform,
  { text: string; color: string; bgColor: string }
> = {
  [CRAWL_BATCH_PLATFORM.TEMU]: {
    text: 'Temu',
    color: 'text-orange-700 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-500/20',
  },
  [CRAWL_BATCH_PLATFORM.AMAZON]: {
    text: 'Amazon',
    color: 'text-blue-700 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-500/20',
  },
}

// ==================== 批次接口定义 ====================

/**
 * 采集批次实体
 */
export interface CrawlBatch {
  /** 批次 ID */
  id: string
  /** 用户 ID */
  userId: string
  /** 平台 */
  platform: CrawlBatchPlatform
  /** 用户备注 */
  remark: string
  /** 批次状态 */
  status: CrawlBatchStatus
  /** 总商品数 */
  totalCount: number
  /** 成功上传数 */
  successCount: number
  /** 失败数 */
  failedCount: number
  /** 创建时间 */
  createdAt: string
  /** 更新时间 */
  updatedAt: string
  /** 完成时间 */
  completedAt: string | null
}

/**
 * 批次列表查询参数
 */
export interface CrawlBatchListParams {
  /** 页码（从 1 开始） */
  current?: number
  /** 每页数量 */
  pageSize?: number
  /** 平台筛选 */
  platform?: CrawlBatchPlatform
  /** 状态筛选 */
  status?: CrawlBatchStatus
  /** 备注搜索关键词（模糊匹配） */
  remark?: string
}

/**
 * 批次列表响应
 */
export interface CrawlBatchListResponse {
  /** 批次列表 */
  list: CrawlBatch[]
  /** 总数 */
  total: number
  /** 当前页码 */
  page: number
  /** 每页数量 */
  page_size: number
}

// ==================== API 响应包装 ====================

/**
 * 批次 API 响应包装结构
 */
export interface ApiResponse<T> {
  code: number
  message: string
  data: T
  trace_id?: string
}
